import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, Bell, LineChart, Check, Users, X, HelpCircle, Minimize2 } from 'lucide-react';
import { authenticatedFetch } from '../utils/api';

const steps = [
  {
    id: 'create_runner',
    title: 'Create your first runner',
    description: 'Set up a runner to start scanning GitHub repositories for sensitive data.',
    path: '/runners',
    buttonText: 'Create runner',
    icon: Play,
    checkCompletion: async () => {
      const response = await authenticatedFetch('/runners?personal=true');
      return response && response.length > 0;
    }
  },
  {
    id: 'view_results',
    title: 'View your first results',
    description: 'Check your dashboard to see discovered sensitive data.',
    path: '/dashboard',
    buttonText: 'View dashboard',
    icon: LineChart,
    checkCompletion: async () => {
      try {
        const response = await authenticatedFetch('/dashboard-stats');
        return response && response.stats && response.stats.total_leaks > 0;
      } catch (error) {
        console.error('Error checking dashboard stats:', error);
        return false;
      }
    }
  },
  {
    id: 'invite_team',
    title: 'Invite colleagues',
    description: 'Collaborate with your team by inviting them to your workspace.',
    path: '/teams',
    buttonText: 'Invite team members',
    icon: Users,
    checkCompletion: async () => {
      try {
        const teams = await authenticatedFetch('/teams');
        
        // Vérifier si teams est un tableau et s'il contient des éléments
        if (!Array.isArray(teams) || teams.length === 0) {
          return false;
        }
        
        // S'assurer que le premier élément a un team_id
        if (!teams[0] || !teams[0].team_id) {
          return false;
        }
        
        const teamId = teams[0].team_id;
        const response = await authenticatedFetch(`/teams/${teamId}/members`);
        
        // Vérifier si response est un tableau et s'il contient plus d'un membre
        return Array.isArray(response) && response.length > 1;
      } catch (error) {
        console.error('Error checking team members:', error);
        return false;
      }
    }
  },
  {
    id: 'configure_alert',
    title: 'Configure notifications',
    description: 'Set up notifications to stay informed about sensitive data discoveries.',
    path: '/settings/notifications',
    buttonText: 'Configure notifications',
    icon: Bell,
    checkCompletion: async () => {
      try {
        const response = await authenticatedFetch('/notifications/preferences');
        // L'étape est complète si l'utilisateur a modifié au moins un paramètre de notification
        return response && (response.emailNotifications || response.inAppNotifications);
      } catch (error) {
        console.error('Error checking notification preferences:', error);
        return false;
      }
    }
  }
];

const OnboardingGuide = ({ onComplete }) => {
  const [completedSteps, setCompletedSteps] = useState(new Set());
  const [isVisible, setIsVisible] = useState(true);
  const [isMinimized, setIsMinimized] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const checkAllSteps = async () => {
      for (const step of steps) {
        try {
          const isCompleted = await step.checkCompletion();
          if (isCompleted) {
            setCompletedSteps(prev => new Set([...prev, step.id]));
          }
        } catch (error) {
          console.error(`Error checking completion for step ${step.id}:`, error);
        }
      }
    };

    checkAllSteps();
    const interval = setInterval(checkAllSteps, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleStepAction = (step) => {
    setIsMinimized(false);
    navigate(step.path);
  };

  const handleDismiss = async () => {
    try {
      await authenticatedFetch('/profile/dismiss-guide', {
        method: 'POST'
      });
      setIsVisible(false);
      if (onComplete) {
        onComplete();
      }
    } catch (error) {
      console.error('Error dismissing guide:', error);
    }
  };

  const handleMinimize = () => {
    setIsMinimized(true);
  };

  const handleMaximize = () => {
    setIsMinimized(false);
  };

  useEffect(() => {
    if (completedSteps.size === steps.length) {
      handleDismiss();
    }
  }, [completedSteps]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isVisible) return null;

  return (
    <AnimatePresence mode="wait">
      {isMinimized ? (
        <div className="fixed bottom-8 right-8 z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            className="relative"
          >
            <motion.div
              initial="initial"
              whileHover="hover"
              animate="initial"
              variants={{
                initial: { width: "48px" },
                hover: { width: "200px" }
              }}
              className="bg-accent hover:bg-accent-hover rounded-full shadow-dropdown cursor-pointer"
              onClick={handleMaximize}
              transition={{ duration: 0.3, ease: "easeOut" }}
              style={{ height: "48px" }}
            >
              <div className="flex items-center h-full px-3 whitespace-nowrap overflow-hidden">
                <HelpCircle size={20} className="flex-shrink-0 text-white" />
                <motion.span
                  variants={{
                    initial: { opacity: 0, x: -10 },
                    hover: { opacity: 1, x: 0 }
                  }}
                  transition={{ duration: 0.2 }}
                  className="ml-3 text-white text-caption font-medium"
                >
                  Setup Guide ({completedSteps.size}/{steps.length})
                </motion.span>
              </div>
            </motion.div>
          </motion.div>
        </div>
      ) : (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          className="fixed bottom-8 right-8 bg-surface-secondary p-6 rounded-lg border border-border shadow-dropdown w-96 z-50"
        >
          <div className="flex justify-between items-start mb-4">
            <h2 className="text-title text-ink">Get the most out of GitGraber</h2>
            <div className="flex space-x-2">
              <button
                onClick={handleMinimize}
                className="text-ink-secondary hover:text-ink transition-base p-1.5 rounded-md hover:bg-surface-tertiary"
                title="Minimize guide"
              >
                <Minimize2 size={18} />
              </button>
              <button
                onClick={handleDismiss}
                className="text-ink-secondary hover:text-ink transition-base p-1.5 rounded-md hover:bg-surface-tertiary"
                title="Dismiss guide permanently"
              >
                <X size={18} />
              </button>
            </div>
          </div>

          <div className="space-y-3">
            {steps.map((step) => {
              const isCompleted = completedSteps.has(step.id);
              const StepIcon = step.icon;

              return (
                <div
                  key={step.id}
                  className={`flex items-start space-x-3 p-3 rounded-md transition-base border ${
                    isCompleted 
                      ? 'bg-success-soft border-success/20' 
                      : 'hover:bg-surface-tertiary border-border'
                  }`}
                >
                  <div className={`p-2 rounded-md flex-shrink-0 ${
                    isCompleted ? 'bg-success-soft text-success' : 'bg-accent-soft text-accent'
                  }`}>
                    {isCompleted ? <Check size={18} /> : <StepIcon size={18} />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-body font-medium text-ink mb-1">{step.title}</h3>
                    <p className="text-caption text-ink-secondary mb-2">{step.description}</p>
                    {!isCompleted && (
                      <button
                        onClick={() => handleStepAction(step)}
                        className="text-caption px-3 py-1.5 bg-accent hover:bg-accent-hover text-white rounded-md transition-base font-medium"
                      >
                        {step.buttonText}
                      </button>
                    )}
                  </div>
                  {isCompleted && (
                    <div className="text-success flex-shrink-0">
                      <Check size={18} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="mt-4 pt-4 border-t border-border flex justify-between items-center">
            <div className="flex space-x-1.5">
              {steps.map((_, index) => (
                <div
                  key={index}
                  className={`h-1.5 rounded-full transition-base ${
                    completedSteps.size > index 
                      ? 'bg-accent w-6' 
                      : 'bg-surface-tertiary w-1.5'
                  }`}
                />
              ))}
            </div>
            <span className="text-caption text-ink-secondary">
              {completedSteps.size} / {steps.length}
            </span>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default OnboardingGuide; 